import React from 'react';
import { formatDateForString } from 'utils/formatter';
import { addSignCommaFormat, commaFormat } from '../new-report/utils/formatter';
import { amountToAmountColor } from '../utils/util';
import { AccountType, AccountTypeLabel } from '../utils/accountType';
import { Side, SideLabel } from '../utils/side';
import { productTypesToString } from '../utils/productTypes';

export const Capsule = ({
  title,
  value,
  isTitleSpaceWide,
  className,
}: {
  title: React.ReactElement;
  value: React.ReactElement;
  isTitleSpaceWide: boolean;
  className?: string | ((prev: string) => string);
}): React.ReactElement => {
  return (
    <div className="w-full">
      <div
        className={`${(typeof className === 'function'
          ? className
          : (prev: string) => `${prev} ${className}`)(
          'rounded border border-gray-500 border-solid flex overflow-hidden'
        )}`}>
        <div
          className={`bg-gray-300 border-gray-500 border-r border-solid pl-2 flex items-center ${
            isTitleSpaceWide ? 'w-24' : 'w-20'
          }`}>
          {title}
        </div>
        <div className="flex-1 flex items-center justify-end pr-2">{value}</div>
      </div>
    </div>
  );
};

export type TradeDetailItemProps = {
  date: string | null;
  symbol: string;
  symbolName: string;
  accountType: number;
  cashMargin: number;
  tradeType: number;
  side: number;
  qty: number | null;
  price: number | null;
  openPriceUSD: number | null;
  profitOrLoss: number | null;
  profitOrLossUSD: number | null;
  productType: number;
  brokerName: string;
};

export const TradeDetailItem = ({
  accountType,
  cashMargin,
  date,
  openPriceUSD,
  price,
  productType,
  profitOrLoss,
  profitOrLossUSD,
  qty,
  side,
  symbol,
  symbolName,
  tradeType,
  brokerName,
}: TradeDetailItemProps): React.ReactElement => {
  const isShowProfitOrLoss = profitOrLoss !== null;
  return (
    <>
      <div className="bg-white flex flex-col p-4">
        <div className="flex flex-row items-center">
          <SideLabel side={side as Side} />
          <div className="ml-2 text-xs text-bluegray-700">
            {productTypesToString(productType, cashMargin)}
          </div>
          <div className="ml-auto text-xs text-gray-500">
            {date === null ? '' : formatDateForString(date, 'yyyy/MM/dd')}
          </div>
        </div>
        <div className="flex flex-row items-center mt-2">
          <AccountTypeLabel accountType={accountType as AccountType} />
          <div className="ml-1 text-sm text-bluegray-500">{brokerName}</div>
        </div>
        <div className="flex flex-row items-center mt-2">
          <Capsule
            title={
              <>
                <div className="text-sm whitespace-no-wrap">数量</div>
              </>
            }
            value={
              <>
                {qty === null
                  ? '---'
                  : commaFormat(qty, { numberOfDecimals: 0 })}
              </>
            }
            isTitleSpaceWide={false}
            className={(prev): string => `${prev} text-sm flex-grow`}
          />
          <div className="w-2" />
          <Capsule
            title={
              <>
                <div className="text-sm whitespace-no-wrap">約定単価</div>
              </>
            }
            value={
              <>
                {price === null
                  ? '---'
                  : commaFormat(price, { numberOfDecimals: 0 })}
              </>
            }
            isTitleSpaceWide={false}
            className={(prev): string => `${prev} text-sm flex-grow`}
          />
        </div>
        {isShowProfitOrLoss && (
          <div className="mt-2">
            <Capsule
              isTitleSpaceWide
              title={
                <>
                  <div className="text-sm">確定損益</div>
                </>
              }
              value={
                <>
                  <div
                    className={`${amountToAmountColor(
                      profitOrLoss ?? 0
                    )} text-base font-bold`}>
                    {addSignCommaFormat(profitOrLoss, { numberOfDecimals: 0 })}
                  </div>
                </>
              }
            />
          </div>
        )}
      </div>
    </>
  );
};

export default TradeDetailItem;
